/**
 * Factor Performance Table
 * Deep-dive table showing ON vs OFF hit rate for each analysis factor toggle.
 * Sortable by column headers, with significance indicators and lift color coding.
 */
import { ArrowDown, ArrowUp, ArrowUpDown, Minus } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface FactorPerformanceRow {
  factor: string;
  column: string;
  enabledHits: number;
  enabledTotal: number;
  enabledRate: number;
  disabledHits: number;
  disabledTotal: number;
  disabledRate: number;
  lift: number;
}

interface FactorPerformanceTableProps {
  data: FactorPerformanceRow[];
  loading: boolean;
}

type SortKey = 'factor' | 'enabledRate' | 'disabledRate' | 'lift';
type SortDir = 'asc' | 'desc';

// =============================================================================
// Helpers
// =============================================================================

function getLiftColor(lift: number): string {
  if (lift >= 0.05) return 'text-theme-success';
  if (lift >= 0.02) return 'text-green-300';
  if (lift > -0.02) return 'text-theme-text-secondary';
  if (lift > -0.05) return 'text-theme-warning';
  return 'text-theme-danger';
}

function getSignificanceIndicator(
  enabledTotal: number,
  disabledTotal: number,
): { label: string; className: string } {
  const minSample = Math.min(enabledTotal, disabledTotal);
  if (minSample >= 50) {
    return { label: 'Solid', className: 'bg-theme-success' };
  }
  if (minSample >= 20) {
    return { label: 'Moderate', className: 'bg-theme-warning' };
  }
  return { label: 'Low', className: 'bg-theme-text-tertiary' };
}

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function formatLift(lift: number): string {
  const pct = lift * 100;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}pp`;
}

// =============================================================================
// Component
// =============================================================================

export function FactorPerformanceTable({
  data,
  loading,
}: FactorPerformanceTableProps): ReactElement {
  const [sortKey, setSortKey] = useState<SortKey>('lift');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'factor' ? 'asc' : 'desc');
    }
  };

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'factor':
          cmp = a.factor.localeCompare(b.factor);
          break;
        case 'enabledRate':
          cmp = a.enabledRate - b.enabledRate;
          break;
        case 'disabledRate':
          cmp = a.disabledRate - b.disabledRate;
          break;
        case 'lift':
          cmp = a.lift - b.lift;
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [data, sortKey, sortDir]);

  if (loading) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-theme-text-secondary mb-3">
          Factor Performance (ON vs OFF)
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-theme-primary" />
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-theme-text-secondary mb-3">
          Factor Performance (ON vs OFF)
        </h3>
        <p className="text-theme-text-tertiary text-sm">No factor data available</p>
      </div>
    );
  }

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) {
      return <ArrowUpDown size={12} className="text-theme-text-tertiary" />;
    }
    return sortDir === 'asc' ? (
      <ArrowUp size={12} className="text-theme-primary" />
    ) : (
      <ArrowDown size={12} className="text-theme-primary" />
    );
  };

  return (
    <div className="bg-theme-surface/50 rounded-lg p-4 flex flex-col @5xl:col-span-2">
      <h3 className="text-sm font-medium text-theme-text-secondary mb-3 shrink-0">
        Factor Performance (ON vs OFF)
      </h3>
      <div className="overflow-auto flex-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-theme-text-secondary text-left border-b border-theme-border">
              <th className="pb-2 font-medium">
                <button
                  type="button"
                  onClick={() => handleSort('factor')}
                  className="flex items-center gap-1 hover:text-theme-text transition-colors"
                >
                  Factor <SortIcon column="factor" />
                </button>
              </th>
              <th className="pb-2 font-medium w-28">
                <button
                  type="button"
                  onClick={() => handleSort('enabledRate')}
                  className="flex items-center gap-1 hover:text-theme-text transition-colors"
                >
                  ON Rate <SortIcon column="enabledRate" />
                </button>
              </th>
              <th className="pb-2 font-medium w-28">
                <button
                  type="button"
                  onClick={() => handleSort('disabledRate')}
                  className="flex items-center gap-1 hover:text-theme-text transition-colors"
                >
                  OFF Rate <SortIcon column="disabledRate" />
                </button>
              </th>
              <th className="pb-2 font-medium w-20">
                <button
                  type="button"
                  onClick={() => handleSort('lift')}
                  className="flex items-center gap-1 hover:text-theme-text transition-colors"
                >
                  Lift <SortIcon column="lift" />
                </button>
              </th>
              <th className="pb-2 font-medium w-16">Sig.</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row) => {
              const sig = getSignificanceIndicator(row.enabledTotal, row.disabledTotal);
              const liftColor = getLiftColor(row.lift);

              return (
                <tr key={row.column} className="border-b border-theme-border/50 last:border-0">
                  <td className="py-2 text-theme-text font-medium">{row.factor}</td>
                  <td className="py-2 text-theme-text-secondary">
                    <span>{formatRate(row.enabledRate)}</span>
                    <span className="text-theme-text-tertiary text-xs ml-1">
                      ({row.enabledTotal})
                    </span>
                  </td>
                  <td className="py-2 text-theme-text-secondary">
                    <span>{formatRate(row.disabledRate)}</span>
                    <span className="text-theme-text-tertiary text-xs ml-1">
                      ({row.disabledTotal})
                    </span>
                  </td>
                  <td className={`py-2 font-medium ${liftColor}`}>
                    <span className="inline-flex items-center gap-1">
                      {row.lift > 0.005 ? (
                        <ArrowUp size={12} />
                      ) : row.lift < -0.005 ? (
                        <ArrowDown size={12} />
                      ) : (
                        <Minus size={12} />
                      )}
                      {formatLift(row.lift)}
                    </span>
                  </td>
                  <td className="py-2">
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-full ${sig.className}`}
                      title={`${sig.label} significance (min ${Math.min(row.enabledTotal, row.disabledTotal)} samples)`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
